import React, { useEffect, useState } from "react";
import { SzBox, SzButton, SzIcon, SzTypographie } from "@suezenv/react-theme-components";
import { useTranslationWithPrefix } from "../../hooks/translation";
import signalService from "../../services/api/signal-service";
import pdfService from "../../services/pdfService";
import {
    ISignalCustomParams,
    SignalChangeActionType,
    SignalRequest,
    StatusChangeActionType
} from "../../type/signal.type";
import SignalHighlight from "./sections/signal-highlight";
import SignalRelatedRequest from "./sections/signal-relatedRequest";
import SignalData from "./sections/signal-data";
import SignalTabs from "./sections/signal-tabs";
import ActionModal, { IValidationActionAttachments, IValidationActionComment } from "./sections/action-modal";
import { UserType } from "../../type/user.type";
import { useIsHasOperatorService, useIsHasSignalService } from "../../hooks/service";
import Unauthorized from "../message/unauthorized";
import "./signal-detail.scss";
import i18n from "../../../i18n";
import { I18nextProvider } from "react-i18next";
import store from "../../store";
import { setComingFromDetail } from "../../store/list/list.reducer";
import { dateToString, stringToDate } from "../../helpers/date.util";
import { useOrganization } from "../../hooks/organization";
import requestTypeReferenceRepository from "../../services/repository/request-type-reference.repository";
import useSignalCategory from "../../hooks/signal-category";
import { RequestTypeReference } from "../../type/organization.type";
import { buildCleanFileName } from "../../helpers/file.util";

interface ISignalDetail {
    signalId: string;
    user: UserType;
    onBackClicked: () => void;
    goSignalMap: (lat: string, lon: string) => void;
    customParams?: ISignalCustomParams;
    customAction: () => void;
}

export const SignalDetail: React.FC<ISignalDetail> = ({
        signalId,
        user,
        onBackClicked,
        goSignalMap,
        customParams,
        customAction
    }) => {
    const [showModal, setShowModal] = useState(false);
    const [tab, setTab] = useState("comment-tab");
    const [modalAction, setModalAction] = useState<StatusChangeActionType|SignalChangeActionType>("comment");
    const [modalValidationMethod, setModalValidationMethod] = useState<any>((): void => {});
    const [signal, setSignal] = useState<SignalRequest>();
    const [requestTypes, setRequestTypes] = useState<RequestTypeReference[]>([]);
    const getSignalCategory = useSignalCategory(requestTypes);
    const organization = useOrganization(user);
    const isHasSignalService = useIsHasSignalService(user);
    const isHasOperatorService = useIsHasOperatorService(user);
    const bodyRef = React.createRef<any>();
    const trans = useTranslationWithPrefix("signalement", "detail_signal");

    const fetchSignal = async (): Promise<void> => {
        const signalRequest = await signalService.getOne(signalId);
        setSignal(signalRequest.data);
    };

    const onBackButtonClicked = (fromDetail: boolean) => {
        store.dispatch(setComingFromDetail({fromDetail: true}))
        onBackClicked()
    }

    const createPdf = () => {
        if (!signal) {
            return;
        }

        const mainElement: HTMLElement = document.getElementById("pdf-export") || document.body;
        const mainElementClone = mainElement.cloneNode(true);
        const hiddenTab = tab === "comment-tab" ? "picture-tab" : "comment-tab";
        const currentTab = tab;
        setTab(hiddenTab);

        const pdfFileName = (): string => {
            const { incrementalId, requestType, createdAt, address } = signal;

            return buildCleanFileName([
                String(incrementalId),
                getSignalCategory(requestType)?.label ?? '',
                address?.city,
                dateToString(stringToDate(createdAt), "DD-MM-YYYY"),
            ]);
        }

        setTimeout(() => {
            const hiddenTabElement: any = document.querySelector(`.${hiddenTab}`);
            const hiddenTabElementClone = hiddenTabElement.cloneNode(true);
            const exportingElement = document.createElement("div");
            mainElementClone.appendChild(hiddenTabElementClone);
            exportingElement.setAttribute("id", "temporay-element");
            exportingElement.appendChild(mainElementClone);
            const tabsActionButton: NodeListOf<HTMLElement> = exportingElement.querySelectorAll(`.tab-action-button`);
            tabsActionButton.forEach((button): void => {
                button.style.display = 'none';
            });
            document.body.appendChild(exportingElement);
            pdfService.createPdf(
                exportingElement,
                pdfFileName(),
                (): void => {
                    const tmpElement = document.getElementById("temporay-element");
                    if (tmpElement) {
                        tmpElement.remove();
                    }
                    setTab(currentTab);
                },
            );
        }, 1000);
    };

    const showActionModal = (actionId: StatusChangeActionType|SignalChangeActionType): void => {
        setModalValidationMethod('attachments' === actionId ? () => updateAttachments : () => updateStatus);
        setModalAction(actionId);
        setShowModal(true);
    };

    /**
     * Handles actions triggered from the ActionModal when the signal status changes or when a comment is added.
     *
     * @see ActionModal
     */
    const updateStatus = async (
        action: StatusChangeActionType,
        data: IValidationActionComment
    ): Promise<void> => {
        if (!signal) {
            return;
        }

        let actionPromise = 'comment' === action
            ? signalService.addComment(signal.id, data.comment)
            : signalService.updateStatus(signal.id, action, data.comment);

        await actionPromise;
        await fetchSignal();
    };

    const updateAttachments = async (
        action: SignalChangeActionType,
        data: IValidationActionAttachments,
    ) => {
        if (!signal || 'attachments' !== action || !data.filesAsBase64.length) {
            return;
        }

        await signalService.addAttachmentsById(signalId, data.filesAsBase64);
        await fetchSignal();
    };

    useEffect((): void => {
        const loadSignal = async (): Promise<void> => {
            await fetchSignal();
        };

        loadSignal().catch(error => {
            if (error?.response?.status === 403) {
                alert("Vous n'avez pas le droit d'accéder à ce signalement.");
                onBackClicked();
            }
        });
    }, [signalId]);

    useEffect((): void => {
        if (!organization) {
            return;
        }

        requestTypeReferenceRepository
            .getAll(organization.id)
            .then(({ data }) => {
                setRequestTypes(data);
            });
    }, [organization]);

    return (
        <I18nextProvider i18n={i18n}>
            {isHasSignalService ? (
                <div className="signal-detail">
                    <div className="signal-detail-container mb-3">
                        <SzBox className={`signal-detail-body mt-3 mb-5 mx-4`} tag="div">
                            <div className="row">
                                <div className="col">
                                    <div className="row mb-4">
                                        <div className="col-auto">
                                            <SzButton
                                                variant="tertiary"
                                                className="mr-3 pl-1"
                                                icon="keyboard-arrow-left"
                                                // tslint:disable-next-line: jsx-no-lambda
                                                onClick={(event) => {
                                                    onBackButtonClicked(true);
                                                }}
                                            >
                                                {trans("back")}
                                            </SzButton>
                                        </div>
                                    </div>
                                    <div className="row header justify-content-between">
                                        <div className="col">
                                            <SzTypographie variant="h1" weight="regular" className="mb-0">
                                                {trans("title")}
                                            </SzTypographie>
                                            <SzTypographie variant="caption" weight="medium" className="mb-0">
                                                <SzIcon className="icon-sm" variant="line" icon="common-file-text" />
                                                {trans("sub_title")}
                                            </SzTypographie>
                                        </div>
                                        {isHasOperatorService && (
                                            <div className="col-auto d-none d-md-flex">
                                                {'function' === typeof goSignalMap
                                                    && signal?.address?.latitude
                                                    && signal?.address?.longitude
                                                    && (
                                                    <SzButton
                                                        variant="tertiary"
                                                        className="mr-3"
                                                        icon="pin-location-1"
                                                        onClick={() => {
                                                            goSignalMap(signal.address.latitude, signal.address.longitude);
                                                        }}
                                                    >
                                                        {trans("view_on_map")}
                                                    </SzButton>
                                                )}
                                                <SzButton
                                                    variant="tertiary"
                                                    className="mr-3"
                                                    icon="download-bottom"
                                                    onClick={createPdf}
                                                >
                                                    {trans("export_signal")}
                                                </SzButton>
                                                {signal?.currentStatus.status.id === "new" && (
                                                    <SzButton
                                                        variant="secondary"
                                                        icon="remove"
                                                        className="mr-3"
                                                        // tslint:disable-next-line: jsx-no-lambda
                                                        onClick={() => showActionModal("rejected")}
                                                    >
                                                        {trans("reject_signal")}
                                                    </SzButton>
                                                )}
                                                {signal?.currentStatus.status.id === "new" && (
                                                    <SzButton
                                                        className="mr-3"
                                                        icon="check-1"
                                                        // tslint:disable-next-line: jsx-no-lambda
                                                        onClick={() => showActionModal("approved")}
                                                    >
                                                        {trans("approuve_signal")}
                                                    </SzButton>
                                                )}
                                                {signal?.currentStatus.status.id === "approved" && (
                                                    <SzButton
                                                        className="mr-3"
                                                        icon="remove-square"
                                                        // tslint:disable-next-line: jsx-no-lambda
                                                        onClick={() => showActionModal("closed")}
                                                    >
                                                        {trans("close_signal")}
                                                    </SzButton>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {signal && (
                                        <div id="pdf-export" ref={bodyRef} className="pdf-export">
                                            <SignalHighlight
                                                signal={signal}
                                                user={user}
                                                updateStatus={updateStatus}
                                                modalAction={modalAction}
                                                showActionModal={showActionModal}
                                            />
                                            <SignalRelatedRequest signalId={signalId}/>
                                            <SignalData signal={signal} />
                                            <SignalTabs
                                                signal={signal}
                                                selectedTab={tab}
                                                changeTab={setTab}
                                                addComment={() => showActionModal('comment')}
                                                addAttachments={() => showActionModal('attachments')}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </SzBox>
                        <ActionModal
                            show={showModal}
                            action={modalAction}
                            setShow={setShowModal}
                            validate={modalValidationMethod}
                            customParams={customParams}
                            customAction={customAction}
                        />
                    </div>
                </div>
            ) : (
                <Unauthorized />
            )}
        </I18nextProvider>
    );
};
